import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import type { VehiclesProviderVehiclesFragment } from './VehiclesProvider.hook'

export type VehiclesContextValue<T extends VehiclesProviderVehiclesFragment> = {
  vehicles: T[]
  loadingVehicles: boolean
  selectedVehicle: T | undefined
  wasVehicleSelectedBySearch: boolean
  selectVehicle: (id: string, deselectIfSelected?: boolean) => void
  searchVehicle: (id?: string) => void
  clearVehicleSelection: () => void
}

type CreateVehiclesProviderProps<T extends VehiclesProviderVehiclesFragment> = {
  useVehiclesQuery: () => {
    vehicles: T[]
    loading: boolean
  }
}

export const createVehiclesProvider = <
  T extends VehiclesProviderVehiclesFragment,
>({
  useVehiclesQuery,
}: CreateVehiclesProviderProps<T>) => {
  const VehiclesContext = createContext<VehiclesContextValue<T> | null>(null)

  const Provider = ({ children }: { children: React.ReactNode }) => {
    const { vehicles, loading: loadingVehicles } = useVehiclesQuery()

    const [selectedVehicleId, setSelectedVehicleId] = useState<string>()
    const [wasVehicleSelectedBySearch, setIsVehicleSelectedViaSearch] =
      useState(false)

    const [searchParams, setSearchParams] = useSearchParams()
    const urlParamVehicleId = searchParams.get('vehicleId')

    const selectVehicle = (id: string, deselectIfSelected = false) => {
      if (selectedVehicleId === id && deselectIfSelected) {
        clearVehicleSelection()
      } else {
        setSearchParams({ vehicleId: id })
        setIsVehicleSelectedViaSearch(false)
      }
    }

    const searchVehicle = (id?: string) => {
      if (id === undefined) {
        setIsVehicleSelectedViaSearch(false)
        return
      }

      if (selectedVehicleId !== id) {
        setSearchParams({ vehicleId: id })
        setIsVehicleSelectedViaSearch(true)
      }
    }

    const clearVehicleSelection = () => {
      setSearchParams({})
      setSelectedVehicleId(undefined)
      setIsVehicleSelectedViaSearch(false)
    }

    const selectedVehicle = useMemo(
      () => vehicles.find(v => v.id === selectedVehicleId) || undefined,
      [selectedVehicleId, vehicles],
    )

    // Sync with URL parameter
    useEffect(() => {
      if (!loadingVehicles) {
        if (urlParamVehicleId) {
          if (vehicles.some(v => v.id === urlParamVehicleId)) {
            setSelectedVehicleId(urlParamVehicleId)
          } else {
            setSearchParams({})
          }
        } else {
          setSelectedVehicleId(undefined)
          setIsVehicleSelectedViaSearch(false)
        }
      }
    }, [urlParamVehicleId, vehicles, loadingVehicles, setSearchParams])

    return (
      <VehiclesContext.Provider
        value={{
          vehicles,
          loadingVehicles,
          selectedVehicle,
          wasVehicleSelectedBySearch,
          selectVehicle,
          searchVehicle,
          clearVehicleSelection,
        }}
      >
        {children}
      </VehiclesContext.Provider>
    )
  }

  const useVehicles = () => {
    const vehiclesContextValue = useContext(VehiclesContext)

    if (vehiclesContextValue === null) {
      throw new Error('useVehicles must be used within VehiclesProvider')
    }

    return vehiclesContextValue
  }

  return { Provider, useVehicles }
}
